import { useState } from 'react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { FcfContributorsResponse } from '@/features/decisions/decisions.api.types';
import { useTenant } from '@/providers/TenantProvider';

export interface UseFcfContributorsParams {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  experimentId: string;
  periodFrom: string;
  periodTo: string;
  pageSize?: number;
  // Case-insensitive SKU substring filter (server-side). '' = no filter.
  skuContains?: string;
}

interface TimedResponse extends FcfContributorsResponse {
  __elapsed_ms: number;
}

// Owns the contributors table's data + pagination state.
export function useFcfContributors({
  scenarioRunId,
  runId,
  refExperimentId,
  experimentId,
  periodFrom,
  periodTo,
  pageSize = 10,
  skuContains = '',
}: UseFcfContributorsParams) {
  const { tenantAlias } = useTenant();
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the search term changes. Render-phase (not an
  // effect) so the page never lands out of range for the new filtered count.
  const [prevSku, setPrevSku] = useState(skuContains);
  if (skuContains !== prevSku) {
    setPrevSku(skuContains);
    setPage(1);
  }

  const query = useQuery<TimedResponse, Error>({
    queryKey: [
      'fcf-contributors',
      tenantAlias,
      scenarioRunId,
      runId,
      refExperimentId,
      experimentId,
      periodFrom,
      periodTo,
      page,
      pageSize,
      skuContains,
    ],
    enabled: Boolean(
      scenarioRunId && runId && refExperimentId && experimentId && periodFrom && periodTo && periodFrom <= periodTo,
    ),
    retry: false,
    staleTime: 30_000,
    // Keep the prior rows on screen while a new search/page loads, so the table
    // doesn't blank to "Loading…" on every debounced keystroke.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const start = performance.now();
      const data = await apiFetch<FcfContributorsResponse>(tenantAlias, ApiEndpoint.SimulationFcfContributors, {
        pathParams: { scenarioRunId, runId },
        query: {
          ref_experiment_id: refExperimentId,
          experiment_id: experimentId,
          period_from: periodFrom,
          period_to: periodTo,
          page,
          page_size: pageSize,
          ...(skuContains ? { sku_contains: skuContains } : {}),
        },
      });
      return { ...data, __elapsed_ms: Math.round(performance.now() - start) };
    },
  });

  return {
    rows: query.data?.rows ?? [],
    netFcfImpact: query.data?.net_fcf_impact ?? null,
    resolved: query.data?.resolved,
    pageInfo: query.data?.page,
    elapsedMs: query.data?.__elapsed_ms,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    page,
    setPage,
    pageSize,
  };
}
